import { useState } from 'react'
import { ClassifierPromptStatus } from '../../../api/client'
import ModelSelect from '../../../components/classifier/ModelSelect'
import { FIELD_CLASS, LABEL_CLASS } from '../../../components/inputs/fieldStyles'
import { ClassifierPromptVersion } from './classifierVersions'
import ThresholdHelp from './ThresholdHelp'

/** The versioned settings a draft can change. A null threshold means the version
 * doesn't define that band — a version may have any combination, like moderation
 * boundaries. */
export type DraftFormValues = {
    prompt: string
    model: string
    borderlineThreshold: number | null
    confirmedThreshold: number | null
}

type Props = {
    version: ClassifierPromptVersion
    saving: boolean
    discarding: boolean
    onSave: (values: DraftFormValues) => void
    onDiscard: () => void
    /** Unsaved edits are handed over so going live can save them first; null when the
     * fields still match the saved draft. */
    onRequestGoLive: (unsaved: DraftFormValues | null) => void
}

// Live and archived versions are read-only, so their controls need dimming.
const READ_ONLY_FIELD_CLASS = `${FIELD_CLASS} disabled:bg-ink-50 disabled:text-ink-500`

/**
 * The selected version's prompt, model and thresholds.
 *
 * Form state lives here and is seeded from `version` on mount, so the caller must key
 * this component on the version it's showing — switching versions in the list then
 * reloads the fields, while a background refetch leaves an in-progress edit alone.
 */
export default function ClassifierVersionPanel({
    version,
    saving,
    discarding,
    onSave,
    onDiscard,
    onRequestGoLive
}: Props) {
    const [values, setValues] = useState<DraftFormValues>(() => ({
        prompt: version.prompt,
        model: version.model,
        borderlineThreshold: version.defaultBorderlineThreshold ?? null,
        confirmedThreshold: version.defaultConfirmedThreshold ?? null
    }))

    // Editing a version that's already live or archived would rewrite history for
    // every profile it classified, so only a draft is editable.
    const isDraft = version.status === ClassifierPromptStatus.Draft
    const isArchived = version.status === ClassifierPromptStatus.Archived

    const isDirty =
        values.prompt !== version.prompt ||
        values.model !== version.model ||
        values.borderlineThreshold !== (version.defaultBorderlineThreshold ?? null) ||
        values.confirmedThreshold !== (version.defaultConfirmedThreshold ?? null)

    function set<K extends keyof DraftFormValues>(key: K, next: DraftFormValues[K]) {
        setValues({ ...values, [key]: next })
    }

    return (
        <div className="rounded-lg border border-arwen-200 border-l-4 border-l-arwen-600 bg-white">
            <div className="flex items-center justify-between border-b border-arwen-200 bg-arwen-50 px-4 py-3">
                <span className="text-sm font-semibold text-arwen-700">
                    Version v{version.version}
                </span>
                {!isDraft && (
                    <span className="text-[11px] font-medium text-ink-500">
                        Read-only — editing a live or archived version isn&rsquo;t allowed
                    </span>
                )}
            </div>
            <div className="space-y-4 p-4">
                <div className="flex flex-wrap gap-4">
                    <ModelSelect
                        value={values.model}
                        disabled={!isDraft}
                        onChange={(next) => set('model', next)}
                        selectClassName={READ_ONLY_FIELD_CLASS}
                    />
                    {/* Thresholds are deliberately uncapped: a signal-only classifier such as
                      Buyer Intent is stopped from moderating anything by setting them above 1
                      (SPAM uses 1.01), so no score can ever cross them. Only the ordering
                      rule in thresholdError applies, and ThresholdHelp explains the rest. */}
                    <div className="flex items-end gap-3">
                        <div>
                            <label htmlFor="borderline" className={LABEL_CLASS}>
                                Borderline
                            </label>
                            <input
                                id="borderline"
                                type="number"
                                min={0}
                                step={0.01}
                                placeholder="None"
                                value={values.borderlineThreshold ?? ''}
                                disabled={!isDraft}
                                onChange={(e) =>
                                    set(
                                        'borderlineThreshold',
                                        e.target.value === '' ? null : Number(e.target.value)
                                    )
                                }
                                className={`${READ_ONLY_FIELD_CLASS} w-24 tabular-nums`}
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmed" className={LABEL_CLASS}>
                                Confirmed
                            </label>
                            <input
                                id="confirmed"
                                type="number"
                                min={0}
                                step={0.01}
                                placeholder="None"
                                value={values.confirmedThreshold ?? ''}
                                disabled={!isDraft}
                                onChange={(e) =>
                                    set(
                                        'confirmedThreshold',
                                        e.target.value === '' ? null : Number(e.target.value)
                                    )
                                }
                                className={`${READ_ONLY_FIELD_CLASS} w-24 tabular-nums`}
                            />
                        </div>
                        {/* Sits level with the inputs rather than the labels, so the row
                            reads as one control group. */}
                        <ThresholdHelp className="mb-2.5" />
                    </div>
                </div>
                <div>
                    <label htmlFor="prompt" className={LABEL_CLASS}>
                        Prompt{' '}
                        <span className="normal-case font-normal text-ink-400">
                            — internal, never shown to clients
                        </span>
                    </label>
                    <textarea
                        id="prompt"
                        rows={10}
                        value={values.prompt}
                        disabled={!isDraft}
                        onChange={(e) => set('prompt', e.target.value)}
                        className={`${READ_ONLY_FIELD_CLASS} font-mono text-xs`}
                    />
                </div>
            </div>
            <div className="flex items-center gap-2 border-t border-ink-100 px-4 py-3">
                {isDraft && (
                    <>
                        <button
                            type="button"
                            onClick={() => onSave(values)}
                            disabled={saving}
                            className="rounded-lg bg-arwen-600 px-3 py-2 text-sm font-semibold text-white hover:bg-arwen-700 disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save draft'}
                        </button>
                        <button
                            type="button"
                            onClick={onDiscard}
                            disabled={discarding}
                            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-semibold text-ink-600 hover:bg-ink-50 disabled:opacity-50"
                        >
                            {discarding ? 'Discarding…' : 'Discard draft'}
                        </button>
                        <button
                            type="button"
                            onClick={() => onRequestGoLive(isDirty ? values : null)}
                            className="ml-auto rounded-lg bg-safe px-3 py-2 text-sm font-bold text-white hover:opacity-90"
                        >
                            Go live with v{version.version}
                        </button>
                    </>
                )}
                {isArchived && (
                    <button
                        type="button"
                        onClick={() => onRequestGoLive(null)}
                        className="ml-auto rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
                    >
                        Roll back to v{version.version}
                    </button>
                )}
            </div>
        </div>
    )
}
